import { ITEM_TYPES, itemTypeLetter, type Condition, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, qs, type CatalogItem, type ImportPreview, type Color, type PurchaseSummary } from '../api';
import { CameraScan } from '../components/CameraScan';
import { ItemImage } from '../components/ItemImage';
import { ExistingBinAlert } from '../components/ExistingBinAlert';
import { useColors, useItemColors, useLocations, useSettings } from '../queries';
import { Badge, Button, Card, Checkbox, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, cn, number, plural, unitPlain } from '../ui';

interface Draft {
  key: number;
  itemType: ItemType;
  itemNo: string;
  itemName: string;
  colorText: string;
  condition: Condition;
  qty: string;
  locationId: string;
  price: string;
}

let nextKey = 1;
const blank = (condition: Condition): Draft => ({ key: nextKey++, itemType: 'PART', itemNo: '', itemName: '', colorText: '', condition, qty: '', locationId: '', price: '' });

const TYPE_LABEL: Record<ItemType, string> = {
  PART: 'Part',
  SET: 'Set',
  MINIFIG: 'Minifigure',
  BOOK: 'Book',
  GEAR: 'Gear',
  CATALOG: 'Catalogue',
  INSTRUCTION: 'Instructions',
  ORIGINAL_BOX: 'Original box',
  UNSORTED_LOT: 'Unsorted lot',
};

const usesColor = (t: ItemType) => t === 'PART' || t === 'UNSORTED_LOT';

function resolveColor(text: string, type: ItemType, colors: Color[] | undefined): Color | null {
  if (!colors) return null;
  if (!usesColor(type)) return colors.find((c) => c.id === 0) ?? null;
  const t = text.trim().toLowerCase();
  if (!t) return null;
  return colors.find((c) => c.name.toLowerCase() === t) ?? (/^\d+$/.test(t) ? (colors.find((c) => c.id === Number(t)) ?? null) : null);
}

export function AddStock() {
  const { id } = useParams();
  const qc = useQueryClient();
  const colors = useColors();
  const locations = useLocations();
  const settings = useSettings();

  const batch = useQuery({
    queryKey: ['batch', id],
    queryFn: () => api<{ batch: { id: number; name: string; purchaseId: number } }>(`/api/v1/batches/${id}`).then((r) => r.batch),
  });
  const purchase = useQuery({
    queryKey: ['purchase-summary', batch.data?.purchaseId],
    enabled: !!batch.data,
    queryFn: () =>
      api<{ purchase: PurchaseSummary & { defaultCondition: Condition } }>(`/api/v1/purchases/${batch.data!.purchaseId}`).then((r) => r.purchase),
  });

  const defaultCondition: Condition = purchase.data?.defaultCondition ?? 'U';
  const [rows, setRows] = useState<Draft[]>(() => [blank('U')]);
  const [done, setDone] = useState<string | null>(null);
  const [tab, setTab] = useState<'catalogue' | 'import'>('catalogue');
  const [pictureColor, setPictureColor] = useState('');

  const update = (key: number, patch: Partial<Draft>) =>
    setRows((rs) => {
      const next = rs.map((r) => (r.key === key ? { ...r, ...patch } : r));
      const last = next[next.length - 1]!;
      return last.itemNo.trim() ? [...next, blank(defaultCondition)] : next;
    });
  const remove = (key: number) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : [blank(defaultCondition)]));

  const filled = rows.filter((r) => r.itemNo.trim() || r.qty);
  const problems = filled.map((r) => {
    const c = resolveColor(r.colorText, r.itemType, colors.data);
    if (!r.itemNo.trim()) return 'Enter an item number';
    if (!c) return usesColor(r.itemType) ? 'Choose a colour' : 'Colour 0 is missing from the catalogue';
    if (!(Number(r.qty) >= 1) || !Number.isInteger(Number(r.qty))) return 'Enter a whole quantity';
    return null;
  });
  const valid = filled.length > 0 && problems.every((p) => p === null);

  const submit = useMutation({
    mutationFn: () =>
      api<{ result: { entries: number; pieces: number; lotsCreated: number; itemsCreated: number } }>(`/api/v1/batches/${id}/stock`, {
        body: {
          rows: filled.map((r) => ({
            itemType: r.itemType,
            itemNo: r.itemNo.trim(),
            itemName: r.itemName.trim() || undefined,
            colorId: resolveColor(r.colorText, r.itemType, colors.data)!.id,
            condition: r.condition,
            qty: Number(r.qty),
            locationId: r.locationId ? Number(r.locationId) : null,
            price: r.price.trim() ? r.price : null,
          })),
        },
      }),
    onSuccess: ({ result }) => {
      setDone(`Added ${plural(result.pieces, 'piece')} in ${plural(result.entries, 'row')}. ${plural(result.lotsCreated, 'new lot')}${result.itemsCreated ? `, ${plural(result.itemsCreated, 'new catalogue item')}` : ''}.`);
      setRows([blank(defaultCondition)]);
      for (const k of ['lots', 'dashboard', 'purchase', 'purchases', 'locations']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  if (batch.isPending || colors.isPending) return <Spinner />;
  if (batch.error || !batch.data) return <ErrorMessage error={batch.error} />;

  const consolidated = settings.data?.inventoryMode === 'consolidated';

  return (
    <>
      <PageHeader
        title="Add stock"
        subtitle={
          <>
            <Link to={`/purchases/${batch.data.purchaseId}`} className="hover:underline">
              {purchase.data?.description || `Purchase ${batch.data.purchaseId}`}
            </Link>{' '}
            / {batch.data.name}
          </>
        }
      />
      <div className="space-y-4">
        {done && <Notice tone="green">{done}</Notice>}
        {purchase.data?.isSystem && (
          <Notice tone="amber">
            This is stock with no known purchase. It comes in at no cost, so it is left out of average costs and profit figures until you know what it cost. Price it as normal.
          </Notice>
        )}

        <CameraScan
          colors={colors.data ?? []}
          onAdd={(pick) =>
            setRows((rs) => {
              const row: Draft = {
                ...blank(defaultCondition),
                itemType: pick.itemType,
                itemNo: pick.itemNo,
                itemName: pick.itemName ?? '',
                colorText: pick.colorName,
                qty: String(pick.qty),
              };
              // Slot it in above the empty row at the end, as the catalogue browser does.
              return [...rs.filter((x) => x.itemNo.trim() || x.qty), row, blank(defaultCondition)];
            })
          }
        />

        <Card
          title="Rows to add"
          actions={
            <Button disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Adding…' : filled.length === 0 ? 'Add to batch' : `Add ${plural(filled.length, 'row')} to batch`}
            </Button>
          }
        >
          <ErrorMessage error={submit.error} />
          <datalist id="color-names">
            {colors.data?.map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          <div className="overflow-x-auto">
            <table className="data w-full min-w-[56rem] text-sm">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Item no.</th>
                  <th>Colour</th>
                  <th>Cond.</th>
                  <th>Qty</th>
                  <th>{consolidated ? 'Location (blank = its home)' : 'Location'}</th>
                  <th>Price</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <RowEditor
                    key={r.key}
                    row={r}
                    colors={colors.data}
                    locations={locations.data}
                    onChange={(p) => update(r.key, p)}
                    onRemove={() => remove(r.key)}
                    problem={problems[filled.indexOf(r)] ?? null}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-500">Tab moves between fields. A new row appears as you type. Leave price empty to price later; cost is spread from the purchase price.</p>
        </Card>

        <div role="tablist" className="flex gap-1 border-b border-slate-200">
          {(
            [
              ['catalogue', 'Find in catalogue'],
              ['import', 'Import a file'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              role="tab"
              type="button"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={cn('-mb-px border-b-2 px-4 py-2 text-sm font-medium', tab === k ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800')}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'catalogue' ? (
          <CataloguePicker
            colors={colors.data ?? []}
            pictureColor={pictureColor}
            setPictureColor={setPictureColor}
            onPick={(item) =>
              setRows((rs) => {
                const row: Draft = { ...blank(defaultCondition), itemType: item.itemType, itemNo: item.itemNo, itemName: '', colorText: usesColor(item.itemType) ? pictureColor : '', qty: '1' };
                const withoutBlank = rs.filter((x) => x.itemNo.trim() || x.qty);
                return [...withoutBlank, row, blank(defaultCondition)];
              })
            }
          />
        ) : (
          <ImportFile batchId={Number(id)} defaultCondition={defaultCondition} onDone={(m) => setDone(m)} />
        )}
      </div>
    </>
  );
}

function RowEditor({
  row,
  colors,
  locations,
  onChange,
  onRemove,
  problem,
}: {
  row: Draft;
  colors: Color[] | undefined;
  locations: { id: number; code: string }[] | undefined;
  onChange: (p: Partial<Draft>) => void;
  onRemove: () => void;
  problem: string | null;
}) {
  const color = resolveColor(row.colorText, row.itemType, colors);
  // Look the item up once typing settles, to show its name or ask for one.
  const key = `${row.itemType}|${row.itemNo.trim()}`;
  const [settled, setSettled] = useState(key);
  useEffect(() => {
    const t = setTimeout(() => setSettled(key), 350);
    return () => clearTimeout(t);
  }, [key]);
  const itemNo = settled.split('|')[1] ?? '';
  const item = useQuery({
    queryKey: ['catalog-exact', settled],
    enabled: !!itemNo,
    queryFn: () => api<{ items: CatalogItem[] }>(`/api/v1/catalog/items${qs({ q: itemNo, itemType: row.itemType, limit: 5 })}`).then((r) => r.items.find((i) => i.itemNo === itemNo) ?? null),
  });
  const lookup = useQuery({
    queryKey: ['lot-lookup', settled, color?.id, row.condition],
    enabled: !!itemNo && !!color && item.data !== null && !item.isPending,
    queryFn: () =>
      api<{ exists: boolean; qtyTotal?: number; locations: { code: string | null; qty: number }[] }>(
        `/api/v1/lots/lookup${qs({ itemType: row.itemType, itemNo, colorId: color!.id, condition: row.condition })}`,
      ),
  });

  // Which colours this part exists in, from BrickLink, asked fresh for each part number that settles.
  const partColours = useItemColors(row.itemType, itemNo, row.itemType === 'PART' && !!itemNo && settled === key);
  const offered = partColours.data;
  const specific = offered?.available && offered.applicable && !offered.notOnBrickLink && offered.colors.length > 0 ? offered.colors : null;
  const listId = `row-colors-${row.key}`;
  const topColours = specific ? [...specific].sort((a, b) => (b.quantity ?? -1) - (a.quantity ?? -1)).slice(0, 8) : [];
  const typedButNotListed = !!specific && !!color && !specific.some((c) => c.id === color.id);

  const unknown = !!itemNo && settled === key && item.data === null && !item.isPending;
  return (
    <>
      <tr className="align-top">
        <td>
          <Select aria-label="Item type" className="w-28" value={row.itemType} onChange={(e) => onChange({ itemType: e.target.value as ItemType })}>
            {ITEM_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </td>
        <td>
          <Input aria-label="Item number" className="w-32" autoComplete="off" value={row.itemNo} onChange={(e) => onChange({ itemNo: e.target.value })} />
        </td>
        <td>
          <Input
            aria-label="Colour"
            className="w-44"
            list={listId}
            disabled={!usesColor(row.itemType)}
            placeholder={usesColor(row.itemType) ? 'Type a colour' : 'not applicable'}
            value={usesColor(row.itemType) ? row.colorText : ''}
            onChange={(e) => onChange({ colorText: e.target.value })}
          />
          {/* Only the colours this part comes in, when BrickLink could be asked; every colour otherwise. */}
          <datalist id={listId}>
            {(specific ?? colors?.filter((c) => c.id !== 0) ?? []).map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
        </td>
        <td>
          <Select aria-label="Condition" className="w-24 min-w-24" value={row.condition} onChange={(e) => onChange({ condition: e.target.value as Condition })}>
            <option value="N">New</option>
            <option value="U">Used</option>
          </Select>
        </td>
        <td>
          <Input aria-label="Quantity" className="w-20" inputMode="numeric" value={row.qty} onChange={(e) => onChange({ qty: e.target.value })} />
        </td>
        <td>
          <Select aria-label="Location" className="w-32" value={row.locationId} onChange={(e) => onChange({ locationId: e.target.value })}>
            <option value="">—</option>
            {locations?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.code}
              </option>
            ))}
          </Select>
        </td>
        <td>
          <Input aria-label="Price" className="w-24" inputMode="decimal" placeholder="0.10" value={row.price} onChange={(e) => onChange({ price: e.target.value })} />
        </td>
        <td className="text-right">
          <Button variant="ghost" small aria-label="Remove row" onClick={onRemove}>
            ✕
          </Button>
        </td>
      </tr>
      {(row.itemNo.trim() || problem) && (
        <tr>
          <td />
          <td colSpan={7} className="pt-0! pb-2! text-xs">
            {/* The grid is wider than a phone, so keep these notes to the visible width and in view as it scrolls. */}
            <div className="sticky left-3 w-[min(42rem,calc(100vw-10rem))]">
            <span className="flex flex-wrap items-center gap-2">
              {item.data && <span className="text-slate-700">{item.data.name}</span>}
              {unknown && (
                <>
                  <Badge tone="amber">Not in catalogue</Badge>
                  <Input aria-label="Name for new item" className="w-64" placeholder="Name it to add it" value={row.itemName} onChange={(e) => onChange({ itemName: e.target.value })} />
                </>
              )}
              {itemNo && settled === key && item.data && (
                <ExistingBinAlert itemType={row.itemType} itemNo={itemNo} colorId={color?.id} condition={row.condition} chosen={row.locationId} onUse={(id) => onChange({ locationId: String(id) })} />
              )}
              {problem && row.qty && <span className="text-red-600">{problem}</span>}
            </span>
            {row.itemType === 'PART' && itemNo && settled === key && (
              <span className="mt-1 flex flex-wrap items-center gap-1.5">
                {partColours.isFetching && <span className="text-slate-500">Asking BrickLink which colours this part comes in…</span>}
                {specific && (
                  <>
                    <span className="text-slate-500">
                      Comes in {specific.length} colour{specific.length === 1 ? '' : 's'}:
                    </span>
                    {topColours.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => onChange({ colorText: c.name })}
                        aria-pressed={color?.id === c.id}
                        className={cn(
                          'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                          color?.id === c.id ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-300 bg-white hover:bg-slate-50',
                        )}
                      >
                        <span className="size-3 rounded-full ring-1 ring-slate-300" style={{ background: c.rgb ? `#${c.rgb}` : '#e2e8f0' }} aria-hidden="true" />
                        {c.name}
                      </button>
                    ))}
                    {specific.length > topColours.length && <span className="text-slate-500">and {specific.length - topColours.length} more in the colour box</span>}
                  </>
                )}
                {typedButNotListed && <span className="text-amber-700">BrickLink does not list {color!.name} for this part. Check the number and colour.</span>}
                {offered?.available && offered.notOnBrickLink && <span className="text-amber-700">BrickLink does not know this part number, so every colour is offered.</span>}
                {offered && !offered.available && offered.reason !== 'not_connected' && <span className="text-amber-700">{offered.message}</span>}
              </span>
            )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CataloguePicker({
  colors,
  pictureColor,
  setPictureColor,
  onPick,
}: {
  colors: Color[];
  pictureColor: string;
  setPictureColor: (v: string) => void;
  onPick: (i: CatalogItem) => void;
}) {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [term, setTerm] = useState({ q: '', type: '' });
  const items = useQuery({
    queryKey: ['catalog-browse', term],
    enabled: term.q.length >= 2,
    queryFn: () => api<{ items: CatalogItem[] }>(`/api/v1/catalog/items${qs({ q: term.q, itemType: term.type, limit: 48 })}`).then((r) => r.items),
  });
  const color = useMemo(() => colors.find((c) => c.name.toLowerCase() === pictureColor.trim().toLowerCase()), [colors, pictureColor]);

  return (
    <Card>
      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setTerm({ q: q.trim(), type });
        }}
      >
        <Field label="Find a part, set or minifigure">
          <Input className="w-72" placeholder="Number or name, e.g. plate 2 x 4" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Any</option>
            {ITEM_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Colour" hint="Shows in the pictures and fills new rows">
          <Input list="color-names" className="w-44" value={pictureColor} onChange={(e) => setPictureColor(e.target.value)} placeholder="Type a colour" />
        </Field>
        <Button type="submit" disabled={q.trim().length < 2}>
          Search
        </Button>
      </form>
      {term.q.length < 2 ? (
        <p className="text-sm text-slate-500">Search, then click an item to put it in the rows above.</p>
      ) : items.isPending ? (
        <Spinner />
      ) : items.data && items.data.length === 0 ? (
        <Empty>
          Nothing in the catalogue matches. Load the catalogue from Channels, or type the item number straight into the rows above and give it a name.
        </Empty>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {items.data?.map((i) => (
            <li key={i.id}>
              <button type="button" onClick={() => onPick(i)} className="flex h-full w-full flex-col items-center gap-1 rounded-lg border border-slate-200 p-2 text-center hover:border-slate-400 hover:bg-slate-50">
                <ItemImage itemType={i.itemType} itemNo={i.itemNo} colorId={color?.id ?? 0} className="size-20" />
                <span className="text-sm font-medium">{i.itemNo}</span>
                <span className="line-clamp-2 text-xs text-slate-500">{i.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ImportFile({ batchId, defaultCondition, onDone }: { batchId: number; defaultCondition: Condition; onDone: (m: string) => void }) {
  const qc = useQueryClient();
  const [content, setContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [remarksAsLocation, setRemarksAsLocation] = useState(false);
  const [defaultType, setDefaultType] = useState<ItemType>('PART');
  const [condition, setCondition] = useState<Condition>(defaultCondition);
  const [createLocations, setCreateLocations] = useState(true);
  const [applyPrices, setApplyPrices] = useState(true);
  const [skipUnresolved, setSkipUnresolved] = useState(false);

  const parse = useMutation({
    mutationFn: (text: string) =>
      api<ImportPreview>('/api/v1/imports/parse', { body: { content: text, options: { remarksAsLocation, defaultItemType: defaultType, defaultCondition: condition } } }),
  });
  const commit = useMutation({
    mutationFn: () =>
      api<{ result: { entries: number; pieces: number; lotsCreated: number; unresolved: unknown[] } }>(`/api/v1/batches/${batchId}/import`, {
        body: { rows: parse.data!.rows, createLocations, applyPrices, skipUnresolved },
      }),
    onSuccess: ({ result }) => {
      onDone(`Imported ${plural(result.pieces, 'piece')} from ${fileName}: ${plural(result.entries, 'row')}, ${plural(result.lotsCreated, 'new lot')}${result.unresolved.length ? `, ${plural(result.unresolved.length, 'row')} skipped` : ''}.`);
      parse.reset();
      setContent(null);
      setFileName('');
      for (const k of ['lots', 'dashboard', 'purchase', 'purchases', 'locations']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  const preview = parse.data;
  return (
    <Card>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Reads BrickStore (.bsx) files, BrickLink inventory XML, and CSV files from BrickLink or a spreadsheet. You see what would happen before anything is added.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <Field label="File">
            <input
              type="file"
              accept=".bsx,.xml,.csv,.tsv,.txt"
              className="block text-sm"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFileName(f.name);
                setContent(await f.text());
                parse.reset();
              }}
            />
          </Field>
          <Field label="Items with no type are">
            <Select value={defaultType} onChange={(e) => setDefaultType(e.target.value as ItemType)}>
              {ITEM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Rows with no condition are">
            <Select value={condition} onChange={(e) => setCondition(e.target.value as Condition)}>
              <option value="N">New</option>
              <option value="U">Used</option>
            </Select>
          </Field>
          <Checkbox label="BrickLink Remarks hold my bin location" checked={remarksAsLocation} onChange={setRemarksAsLocation} />
          <Button disabled={!content || parse.isPending} onClick={() => parse.mutate(content!)}>
            {parse.isPending ? 'Reading…' : 'Read file'}
          </Button>
        </div>
        <ErrorMessage error={parse.error} />

        {preview && (
          <div className="space-y-3">
            <Notice tone={preview.summary.unresolved > 0 ? 'amber' : 'green'}>
              {preview.format.toUpperCase()}: {number(preview.summary.total)} rows, {number(preview.summary.pieces)} pieces.{' '}
              {preview.summary.ready} ready{preview.summary.newItems > 0 && `, ${preview.summary.newItems} will create new catalogue items`}
              {preview.summary.unresolved > 0 && `, ${preview.summary.unresolved} cannot be matched`}
              {preview.skipped.length > 0 && `, ${preview.skipped.length} unreadable lines skipped`}.
              {preview.locations.filter((l) => !l.exists).length > 0 && ` ${preview.locations.filter((l) => !l.exists).length} new locations.`}
            </Notice>
            {preview.warnings.map((w) => (
              <p key={w} className="text-xs text-amber-700">
                {w}
              </p>
            ))}
            <div className="max-h-96 overflow-auto rounded-md border border-slate-200">
              <table className="data w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th>Line</th>
                    <th>Item</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Price</th>
                    <th>Location</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 500).map((r) => (
                    <tr key={r.line}>
                      <td className="text-slate-400">{r.line}</td>
                      <td>
                        {itemTypeLetter(r.itemType)} {r.itemNo}
                        {r.itemName && <div className="max-w-48 truncate text-xs text-slate-500">{r.itemName}</div>}
                      </td>
                      <td>{r.resolution.colorName ?? r.colorName ?? '?'}</td>
                      <td>{r.condition}</td>
                      <td className="text-right tabular-nums">{r.qty}</td>
                      <td className="text-right tabular-nums">{r.price ? unitPlain(r.price) : '—'}</td>
                      <td>{r.locationCode ?? '—'}</td>
                      <td>
                        {r.resolution.status === 'ready' && <Badge tone="green">{r.resolution.existing ? `adds to ${r.resolution.existing.qty}` : 'new lot'}</Badge>}
                        {r.resolution.status === 'new_item' && <Badge tone="blue">new item</Badge>}
                        {r.resolution.status === 'unknown_item' && <Badge tone="red">not in catalogue</Badge>}
                        {r.resolution.status === 'unknown_color' && <Badge tone="red">colour unknown</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.rows.length > 500 && <p className="text-xs text-slate-500">Showing the first 500 rows of {number(preview.rows.length)}.</p>}
            <div className="flex flex-wrap items-center gap-4">
              <Checkbox label="Create locations that do not exist" checked={createLocations} onChange={setCreateLocations} />
              <Checkbox label="Use prices from the file for lots without one" checked={applyPrices} onChange={setApplyPrices} />
              {preview.summary.unresolved > 0 && <Checkbox label="Skip rows that cannot be matched" checked={skipUnresolved} onChange={setSkipUnresolved} />}
              <Button disabled={commit.isPending || preview.rows.length === 0 || (preview.summary.unresolved > 0 && !skipUnresolved)} onClick={() => commit.mutate()}>
                {commit.isPending ? 'Importing…' : `Import ${number(preview.rows.length - (skipUnresolved ? preview.summary.unresolved : 0))} rows`}
              </Button>
            </div>
            <ErrorMessage error={commit.error} />
          </div>
        )}
      </div>
    </Card>
  );
}
